'use client';

import { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ModalShellProps {
  /** Open-state flag (drives the enter/exit transition classes). */
  visible: boolean;
  onClose: () => void;
  /** id of the heading that labels the dialog. */
  labelledBy: string;
  closeLabel: string;
  /** Size / radius utilities for the dialog panel. */
  dialogClassName?: string;
  children: React.ReactNode;
}

/**
 * Shared modal chrome: centered backdrop + dialog with origin-center
 * scale/opacity transition, body scroll-lock, focus trap, Escape-to-close, and
 * a 44px close button. Mounts only while open (the parent gates on `render`),
 * so its mount/unmount lifecycle handles the scroll-lock and focus restore.
 */
export default function ModalShell({
  visible,
  onClose,
  labelledBy,
  closeLabel,
  dialogClassName = '',
  children,
}: ModalShellProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    // Close on click outside the dialog (no click handler on the backdrop div
    // itself, so non-interactive elements keep no mouse-only listeners).
    const handlePointerDown = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handlePointerDown);
      document.body.style.overflow = 'unset';
      previouslyFocused?.focus();
    };
  }, []);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/95 backdrop-blur-[20px] transition-opacity duration-200 ease-out motion-reduce:transition-none ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={`relative w-full max-h-[90vh] overflow-y-auto bg-surface shadow-dramatic origin-center transition duration-200 ease-out motion-reduce:transition-none motion-reduce:scale-100 ${
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        } ${dialogClassName}`}
      >
        <button
          ref={closeButtonRef}
          onClick={onClose}
          aria-label={closeLabel}
          className="absolute top-4 right-4 z-10 flex h-11 w-11 items-center justify-center bg-background/80 backdrop-blur-sm rounded-full text-foreground hover:text-primary transition-colors duration-300 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <Icon name="XMarkIcon" size={24} />
        </button>

        {children}
      </div>
    </div>
  );
}
